import {
    Component,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Renderer2,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_ASYNC_VALIDATORS,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgControl,
    NgForm,
    RequiredValidator,
    Validators,
} from '@angular/forms';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { CommonService } from './common.service';
import { RequiredWhenValidatorDirective } from './common.validator';

export interface CmpsFormField {
    id?: string;
    name: string;
    label: string;
    placeholder?: string;
    validators?: any[];
    validationMsgs?: { [key: string]: any };
    validatorDirectives?: any[];
}

export interface CmpsFormFields {
    [key: string]: CmpsFormField;
}

export interface CmpsForm {
    bind: () => void;

    unbind: () => void;

    isValid: () => boolean;

    validateField: (field: CmpsFormField) => void;

    validate: () => void;

    hasErrors: () => boolean;
}

export abstract class AbstractCmpsForm implements CmpsForm {
    form: NgForm;
    warnings: any;
    errors: any;
    serverError: any;
    submitted: boolean;
    private _sub: Subscription;

    constructor(
        public fields: CmpsFormFields,
        protected _translateService: TranslateService,
    ) {
        this.warnings = {};
        this.errors = {};
        this.clearWarningsErrors();
    }

    static getFieldElement(
        form: AbstractCmpsForm | string,
        field: CmpsFormField | string,
    ): HTMLElement {
        const formLookup = typeof form === 'string' ? form : form.name;
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const look = (lookup: string, byId: boolean, byName: boolean) => {
            let el;
            if (byId) {
                el = document.querySelector(
                    `form[name="${formLookup}"] [id="${lookup}"]`,
                );
            }
            if (!el && byName) {
                el = document.querySelector(
                    `form[name="${formLookup}"] [name="${lookup}"]`,
                );
            }
            return el;
        };
        let el;
        if (typeof field === 'string') {
            el = look(field, true, true);
        } else {
            if (field.id) {
                el = look(field.id, true, false);
            }
            if (!el) {
                el = look(field.name, false, true);
            }
        }
        if (!el) {
            console.error(`element not found`, form, field);
        }
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return <HTMLElement>el;
    }

    abstract get name(): string;

    protected _getControl(field: CmpsFormField | string): AbstractControl {
        if (!field || (typeof field === 'string' && field.length === 0))
            return null;
        const path = typeof field === 'string' ? field : field.id || field.name;
        const control = this.form.form.get(path);
        if (!control) {
            console.warn(`control at '${path}' not found`);
        }
        return control;
    }

    protected _validateFieldConfig(field: CmpsFormField): void {
        const control: AbstractControl = this._getControl(field);
        if (!control) {
            throw new Error(`field '${field.name}' control not found`);
        }
        const willValidate =
            control.validator ||
            (field.validators && field.validators.length !== 0);
        if (willValidate && !field.id) {
            throw new Error(
                `field '${field.name}' has validators and must have an id`,
            );
        }
        if (field.id) {
            const pathList = field.id.split('.');
            if (pathList.length > 1) {
                pathList.pop();
                const path = pathList.join('.');
                if (!this._getControl(path)) {
                    throw new Error(
                        `group '${path}' for field '${field.name}' not found`,
                    );
                }
            }
        }
    }

    protected _setValidators(field: CmpsFormField): void {
        const control: AbstractControl = this._getControl(field);
        if (control) {
            let vs = [];
            if (control.validator) {
                vs.push(control.validator);
            }
            if (field.validators) {
                vs = vs.concat(field.validators);
            }
            control.setValidators(vs);
            control.updateValueAndValidity();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    protected _markIfRequired(field: CmpsFormField) {
        let vs = [];
        if (field.validators) {
            vs = vs.concat(field.validators);
        }
        if (field.validatorDirectives) {
            vs = vs.concat(field.validatorDirectives);
        }

        const required =
            vs.findIndex((value) => {
                return (
                    value === Validators.required ||
                    value instanceof RequiredValidator ||
                    (value instanceof RequiredWhenValidatorDirective &&
                        value.markAsRequired)
                );
            }) !== -1;
        if (!required) return;

        const el = AbstractCmpsForm.getFieldElement(this, field);
        if (!el) return;

        el.setAttribute('aria-required', 'true');

        // TODO rewrite
        let labelEl: Element;
        let parent = el;
        for (let i = 0; i < 5; i++) {
            if (parent.parentElement.tagName === 'LABEL') {
                labelEl = parent.parentElement;
            } else if (parent.parentElement.children[0].tagName === 'LABEL') {
                labelEl = parent.parentElement.children[0];
            }
            if (!labelEl) {
                parent = parent.parentElement;
            } else {
                break;
            }
        }

        if (
            !labelEl ||
            labelEl.hasAttribute('noMarkAsRequired') ||
            labelEl.querySelector('span.text-danger')
        )
            return;

        const span = document.createElement('span');
        span.setAttribute('aria-hidden', 'true');
        span.classList.add('text-danger');
        span.textContent = '*';
        labelEl.appendChild(span);
    }

    protected _renderMsg(
        field: CmpsFormField,
        msg: string,
        errors: any,
    ): string {
        if (!msg) return '';
        if (msg.length === 0) return msg;
        const fieldLabel =
            !field.label || field.label.length === 0
                ? ''
                : this._translateService.instant(field.label);
        msg = this._translateService.instant(msg);
        msg = msg.replace(/{{field_label}}/g, fieldLabel);
        if (typeof errors === 'object') {
            for (const key in errors) {
                let errorMsg = errors[key];
                if (!errorMsg) continue;
                if (typeof errorMsg !== 'string') {
                    try {
                        errorMsg = errorMsg.toString();
                    } catch (err) {
                        console.log("can't cast to string");
                        continue;
                    }
                }
                if (errorMsg.length === 0) continue;
                errorMsg = this._translateService.instant(errorMsg);
                msg = msg.replace(new RegExp(`{{${key}}}`, 'g'), errorMsg);
            }
        }
        return msg;
    }

    protected _mapMsgs(
        field: CmpsFormField,
        fieldKey: string,
        dest: any,
        src: any,
    ): void {
        for (const key in src) {
            const msg = !field.validationMsgs
                ? null
                : field.validationMsgs[key];
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            dest[fieldKey] += this._renderMsg(field, msg, src[key]) + ' ';
        }
        dest[fieldKey] = dest[fieldKey].trim();
    }

    protected _setWarnings(fieldKey: string): void {
        this.warnings[fieldKey] = '';
        const field = this.fields[fieldKey];
        if (!field.validatorDirectives) return;
        const control: AbstractControl = this._getControl(field);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (control && control.valid) {
            const warnings = field.validatorDirectives
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-prototype-builtins
                .filter(
                    (value) =>
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        value &&
                        // eslint-disable-next-line no-prototype-builtins
                        value.hasOwnProperty('warnings') &&
                        value.warnings != null,
                )
                .map((value) => value.warnings)
                .reduce((a, b) => Object.assign(a, b), {});
            this._mapMsgs(field, fieldKey, this.warnings, warnings);
        }
    }

    protected _setErrors(fieldKey: string): void {
        this.errors[fieldKey] = '';
        const field = this.fields[fieldKey];
        const control: AbstractControl = this._getControl(field);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (control && control.dirty && !control.valid) {
            this._mapMsgs(field, fieldKey, this.errors, control.errors);
        }
    }

    reset(): void {
        this.submitted = false;
        this.serverError = '';
        this.clearWarningsErrors();

        if (!this.form || !this.form.form) return;
        this.form.form.markAsPristine();
        this.form.form.markAsUntouched();
        this.form.form.updateValueAndValidity();
    }

    bind(): void {
        Object.values(this.fields).forEach((field) => {
            this._validateFieldConfig(field);
            this._setValidators(field);
            this._markIfRequired(field);
        });

        // TODO remove
        // ie11
        // https://connect.microsoft.com/IE/feedback/details/2014435/ie11-destroying-an-iframe-containing-input-fields-causes-future-inputs-to-be-inaccessible-by-mouse
        // https://github.com/JasonTarka/ie11-input-bug-poc
        // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/11492484/
        // const el = <HTMLElement> document.querySelector(`form[name="${this.name}"]`);
        // if (el) {
        //     const pageX = window.pageXOffset;
        //     const pageY = window.pageYOffset;
        //     const input = document.createElement('input');
        //     input.setAttribute('type', 'text');
        //     input.style.width = '0';
        //     input.style.height = '0';
        //     if (el.children.length !== 0) {
        //         el.insertBefore(input, el.children[0]);
        //     } else {
        //         el.appendChild(input);
        //     }
        //     const prevFocus = <HTMLElement> document.activeElement;
        //     input.focus();
        //     prevFocus.focus();
        //     el.removeChild(input);
        //     window.scrollTo(pageX, pageY);
        // }

        const fieldKeys = Object.keys(this.fields);
        this._sub = this.form.statusChanges.subscribe((data) => {
            if (!this.form || !this.form.form) return;
            fieldKeys.forEach((fieldKey) => {
                this._setWarnings(fieldKey);
                this._setErrors(fieldKey);
            });
        });

        this.reset();
    }

    unbind(): void {
        this.reset();
        if (this._sub) {
            this._sub.unsubscribe();
        }
        this.form = null;
    }

    isValid(): boolean {
        return !this.form || !this.form.form ? false : this.form.valid;
    }

    validateField(field: CmpsFormField | string): void {
        if (!this.form || !this.form.form) return;
        if (!(typeof field === 'string') && !field.id) {
            console.warn(
                `field '${field.name}' passed to validateField should have an id`,
            );
        }
        const control: AbstractControl = this._getControl(field);
        if (control && !control.valid) {
            control.markAsDirty();
            control.updateValueAndValidity();
        }
    }

    validate(): void {
        if (!this.form || !this.form.form) return;
        Object.values(this.fields).forEach((field) =>
            this.validateField(field),
        );
    }

    hasErrors(): boolean {
        return Object.values(this.errors).filter((value) => !!value).length > 0;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    clearWarningsErrors() {
        Object.keys(this.fields).forEach((key) => {
            this.warnings[key] = '';
            this.errors[key] = '';
        });
    }

    // @deprecated
    // if more fields require this consider using focus = {} and ValidationIndicatorDirective to store field focus status
    fieldHasFocus(field: CmpsFormField | string): boolean {
        if (!this.form || !this.form.form) return;
        if (!(typeof field === 'string') && !field.id) {
            console.warn(
                `field '${field.name}' passed to fieldHasFocus should have an id`,
            );
        }
        return (
            document.activeElement ===
            AbstractCmpsForm.getFieldElement(this, field)
        );
    }
}

@Directive({
    selector: '[ngModel][field]',
})
export class FieldConfigInjectorDirective implements OnInit {
    @Input()
    field: CmpsFormField;

    constructor(
        private readonly _el: ElementRef,
        private readonly _renderer: Renderer2,
        @Optional() @Inject(NG_VALIDATORS) private readonly _validators,
        @Optional()
        @Inject(NG_ASYNC_VALIDATORS)
        private readonly _asyncValidators,
    ) {}

    ngOnInit(): void {
        if (!this.field) {
            console.warn('no field config assigned', this._el.nativeElement);
        } else {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const el = <HTMLElement>this._el.nativeElement;
            if (el.tagName === 'INPUT' && this.field.id) {
                this._renderer.setAttribute(el, 'id', this.field.id);
            }
        }

        if ((this._validators || this._asyncValidators) && this.field) {
            let vs = [];
            if (this._validators) {
                vs = vs.concat(this._validators);
            }
            if (this._asyncValidators) {
                vs = vs.concat(this._asyncValidators);
            }
            this.field.validatorDirectives = vs;
        }
    }
}

@Directive({
    selector: '[ngModel]',
    host: {
        '(blur)': 'onBlur($event)',
    },
})
export class ValidateOnBlurDirective {
    constructor(private readonly _control: NgControl) {}

    onBlur($event): void {
        if (!this._control || !this._control.control) return;

        // see AbstractCmpsForm.validateField()
        if (!this._control.control.valid) {
            this._control.control.markAsDirty();
            this._control.control.updateValueAndValidity();
        }
    }
}

@Directive({
    selector: 'input[ngModel]:not([ngbDatepicker])',
    host: {
        '(focus)': 'onFocus($event)',
        '(blur)': 'onBlur($event)',
    },
})
export class ValidationIndicatorDirective implements OnInit, OnDestroy {
    private readonly _nEl: HTMLElement;
    private _sub: Subscription;

    constructor(
        private readonly _el: ElementRef,
        private readonly _control: NgControl,
    ) {
        this._nEl = this._el.nativeElement;
    }

    ngOnInit(): void {
        this._sub = this._control.control.statusChanges.subscribe((status) => {
            if (document.activeElement === this._nEl) return;
            this.onFocus(null);
            this.onBlur(null);
        });
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }

    static clear(el: HTMLElement): void {
        if (!el) return;
        // args list not supported in ie11
        ['is-valid', 'is-invalid'].forEach((value) =>
            el.classList.remove(value),
        );
    }

    onFocus($event): void {
        ValidationIndicatorDirective.clear(this._nEl);
    }

    onBlur($event): void {
        if (!this._control || !this._control.control) return;
        if (
            this._control.control.validator &&
            this._control.control.touched &&
            !this._control.control.disabled
        ) {
            this._nEl.classList.add(
                this._control.control.valid ? 'is-valid' : 'is-invalid',
            );
        }
    }
}

@Component({
    selector: 'cmps-entity-selector',
    template: ` <ng-select
        [options]="opts"
        [allowClear]="true"
        (closed)="blur.emit($event)"
        [id]="field.id"
        [placeholder]="field.placeholder | translate"
        [ariaLabelledBy]="ariaLabelledBy"
        [ngModel]="value == null ? null : [value]"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onChange($event === '' ? null : $event)"
    ></ng-select>`,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SuperGroupSelectorComponent),
            multi: true,
        },
    ],
})
export class SuperGroupSelectorComponent
    implements OnInit, OnDestroy, ControlValueAccessor
{
    @Input() field: CmpsFormField;
    @Input() obs: Observable<any>;
    @Input() ariaLabelledBy: string;
    @Output() blur: EventEmitter<any>;

    private _sub: Subscription;
    opts: IOption[];
    private _valueDeferred: number;
    value: number;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _onChange = (_: any) => {};

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _onTouched = () => {};

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onChange = (event: number) => {
        if (this._valueDeferred) {
            this.value = this._valueDeferred;
            this._valueDeferred = null;
            return;
        }
        // eslint-disable-next-line eqeqeq
        if (event == this.value) return;
        this.value = event;
        this._onChange(this.value);
    };

    constructor(
        private readonly _el: ElementRef,
        protected _service: CommonService,
    ) {
        this.blur = new EventEmitter();
    }

    ngOnInit(): void {
        if (!this.field) {
            console.warn('no field config assigned', this._el.nativeElement);
        }

        this._sub = this.obs
            .pipe(
                map((data) =>
                    data.map((value) => {
                        return {
                            label: value.label,
                            value: value.id,
                        };
                    }),
                ),
            )
            // eslint-disable-next-line no-return-assign, n/handle-callback-err
            .subscribe(
                (opts) => (this.opts = opts),
                // eslint-disable-next-line n/handle-callback-err
                (error) => (this.opts = []),
            );
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }

    writeValue(obj: any): void {
        if (!this._sub.closed) {
            this._valueDeferred = obj;
        } else {
            this.value = obj;
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function fixNgSelects(el, form) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const els: Element[] = el.querySelectorAll(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `form[name=${form.name}] ng-select`,
    );
    for (let i = 0; i < els.length; i++) {
        const el = els[i];
        const control = form.form.form.get(el.getAttribute('name'));
        if (control) {
            control.markAsPristine({ onlySelf: true });
        }
    }
}
